/**
 * Bible Mysteries Decoder — FINAL simplified overlay set (2026-07-18, Yash's cut).
 * Collapsed from the 11-component kit down to exactly what Jyotirlinga uses:
 * ONE top-left fact tag + ONE bottom "big moment" treatment + CTA (separate, existing).
 * Tested on a BRIGHT plate (W01 Kadosh Sinai daylight scene) on purpose — this is the
 * legibility stress test. Less padding than the first pass, and every text element
 * carries a real drop-shadow (not a big opaque panel) so it survives a bright sky.
 */
import { AbsoluteFill, Img, staticFile, useCurrentFrame, interpolate, Easing } from "remotion";
import { loadFont as loadMono } from "@remotion/google-fonts/IBMPlexMono";
import { loadFont as loadSans } from "@remotion/google-fonts/Inter";

const { fontFamily: mono } = loadMono("normal", { weights: ["400", "500", "600"], subsets: ["latin"] });
const { fontFamily: sans } = loadSans("normal", { weights: ["400", "600", "700", "800"], subsets: ["latin"] });

const INK = "#ffffff";
const INK_DIM = "#e4e7ea";
const ACCENT = "#8fd6c4";
// the shadow stack that keeps white text legible over a bright sky — no big panel needed
const TEXT_SHADOW = "0 2px 6px rgba(0,0,0,0.95), 0 1px 3px rgba(0,0,0,0.9), 0 0 22px rgba(0,0,0,0.55)";

export const W01_BRIGHT_PLATE = staticFile("w01_kadosh_bright.jpg");

// Logo watermark — same convention as jyotirlinga's LogoWatermark.tsx (fade in/out,
// top %, right px), sized 90px for the 1280x704 (720p) working frame per Yash's spec.
// Matches the CURRENT jyotirlinga/Deva Yatra convention exactly — modal_factory_render.py
// line 1518, the live Kedarnath Panch Kedar entrypoint: "LOGO_W = 270  # 90px @1x x 3
// (scale=3) -> 270px @4K". So 90px @ the 1280x704 base IS correct. Position top:20
// right:20 (ffmpeg "overlay=W-w-20:20", the shared composite convention).
const BdLogoWatermark: React.FC = () => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 36], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  // jesus_black_logo_4K.png is 645x1090 (tall, not square) — matches ffmpeg's
  // "scale=90:-1" behavior: fix WIDTH at 90px, let height auto-scale to the
  // real aspect ratio instead of forcing a square box (which would pillarbox it).
  return (
    <Img
      src={staticFile("jesus_black_logo_4K_2.png")}
      style={{ position: "absolute", top: 20, right: 20, width: 70, height: "auto", opacity: fadeIn * 0.85 }}
    />
  );
};

const Plate: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AbsoluteFill style={{ background: "#000" }}>
    <Img src={W01_BRIGHT_PLATE} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
    <BdLogoWatermark />
    {children}
  </AbsoluteFill>
);

const useFadeIn = (delay: number, dur = 14) => {
  const frame = useCurrentFrame();
  return interpolate(frame, [delay, delay + dur], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });
};

// ═══ SLOT 1 — TOP-LEFT FACT TAG (rotates: citation / date / person — same look) ═══
export const FactTag: React.FC<{
  kicker: string; value: string; startFrame?: number;
}> = ({ kicker, value, startFrame = 0 }) => {
  const frame = useCurrentFrame() - startFrame;
  const inA = interpolate(frame, [3, 14], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const slideY = interpolate(frame, [3, 16], [-8, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });
  if (frame < 0) return null;
  return (
    <div style={{ position: "absolute", top: 40, left: 44, opacity: inA, transform: `translateY(${slideY}px)` }}>
      <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 2, color: ACCENT, textTransform: "uppercase", textShadow: TEXT_SHADOW }}>
        {kicker}
      </div>
      <div style={{ fontFamily: sans, fontSize: 20, fontWeight: 700, color: INK, marginTop: 2, textShadow: TEXT_SHADOW }}>
        {value}
      </div>
    </div>
  );
};

// ═══ SLOT 2 — BOTTOM BIG MOMENT (rotates: root-decode / quote / myth-bust) ═══
export const BigMoment: React.FC<{
  kicker: string; headline: string; sub?: string; startFrame?: number;
}> = ({ kicker, headline, sub, startFrame = 0 }) => {
  const frame = useCurrentFrame() - startFrame;
  const inA = interpolate(frame, [4, 18], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const slideY = interpolate(frame, [4, 20], [10, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });
  if (frame < 0) return null;
  return (
    <div style={{ position: "absolute", left: 44, right: 44, bottom: 44, opacity: inA, transform: `translateY(${slideY}px)` }}>
      <div style={{ fontFamily: mono, fontSize: 12, letterSpacing: 2.5, color: ACCENT, textTransform: "uppercase", textShadow: TEXT_SHADOW, marginBottom: 4 }}>
        {kicker}
      </div>
      <div style={{ fontFamily: sans, fontSize: 32, fontWeight: 800, color: INK, textShadow: TEXT_SHADOW, lineHeight: 1.1 }}>
        {headline}
      </div>
      {sub && (
        <div style={{ fontFamily: sans, fontSize: 15, color: INK_DIM, textShadow: TEXT_SHADOW, marginTop: 6 }}>
          {sub}
        </div>
      )}
    </div>
  );
};

// ═══ W01 FULL TEST — both slots together on the bright Sinai plate ═══
export const W01_OverlayTest_Citation: React.FC = () => (
  <Plate>
    <FactTag kicker="Citation" value="Exodus 3:5" startFrame={0} />
  </Plate>
);

export const W01_OverlayTest_Reveal: React.FC = () => (
  <Plate>
    <BigMoment kicker="The root" headline="qedeshah" sub="Same root, Q D SH, the word your translators called shrine prostitute" startFrame={0} />
  </Plate>
);

export const W01_OverlayTest_Both: React.FC = () => (
  <Plate>
    <FactTag kicker="Citation" value="Exodus 3:5" startFrame={0} />
    <BigMoment kicker="The root" headline="qedeshah" sub="Same root, Q D SH, the word your translators called shrine prostitute" startFrame={20} />
  </Plate>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
